<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { aboutCommitments, aboutValues } from '../data/landing.content'
import { useLandingScroll } from '../composables/useLandingScroll'
import WlIcon from './WlIcon.vue'

const { registerReveal } = useLandingScroll()

/**
 * Seccion "Sobre nosotros" en formato hero.
 *
 * Fondo fotografico con velo oscuro y dos capas de movimiento: un ken burns
 * lento (zoom) y un parallax ligado al scroll. El parallax se calcula con
 * una variable CSS (`--wl-about-shift`) que el keyframe consume, de modo que
 * el scroll y la animacion no se pelean por la propiedad `transform`.
 *
 * Con `prefers-reduced-motion` no hay movimiento: el fondo queda estatico y
 * el texto se lee igual.
 */
const section = ref<HTMLElement | null>(null)
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

let ticking = false

function updateShift() {
  ticking = false
  const el = section.value
  if (!el || reduceMotion.matches) return
  const rect = el.getBoundingClientRect()
  // 0 cuando la seccion entra por abajo, 1 cuando ya sale por arriba.
  const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
  const shift = Math.round((progress - 0.5) * 120)
  el.style.setProperty('--wl-about-shift', `${shift}px`)
}

function onScroll() {
  if (ticking) return
  ticking = true
  window.requestAnimationFrame(updateShift)
}

onMounted(() => {
  updateShift()
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onScroll)
})
</script>

<template>
  <section ref="section" class="wl-about-hero" id="nosotros">
    <div class="wl-about-hero-bg" aria-hidden="true"></div>
    <div class="wl-about-hero-veil" aria-hidden="true"></div>

    <div class="container">
      <div class="wl-about-hero-inner">
        <div class="section-tag reveal" :ref="registerReveal">Sobre nosotros</div>

        <h2 class="wl-about-hero-title reveal" :ref="registerReveal">
          Construyendo el futuro<br />educativo de <span class="gradient-text-light">México</span>
        </h2>

        <div class="wl-acerca-mission-card reveal" :ref="registerReveal">
          <span class="wl-amc-icon"><WlIcon name="rocket" /></span>
          <div class="wl-amc-content">
            <h3>Nuestra misión</h3>
            <p>
              Democratizar el acceso a una preparación académica de calidad para que cada estudiante mexicano pueda
              cumplir sus metas educativas.
            </p>
          </div>
        </div>

        <div class="wl-acerca-values reveal" :ref="registerReveal">
          <div v-for="value in aboutValues" :key="value.title" class="wl-acerca-value">
            <div class="wl-av-icon"><WlIcon :name="value.icon" /></div>
            <h4>{{ value.title }}</h4>
            <p>{{ value.description }}</p>
          </div>
        </div>

        <div class="wl-about-commitments reveal" :ref="registerReveal">
          <h3 class="wl-ac-title">Nuestro compromiso</h3>
          <ul class="wl-ac-list">
            <li v-for="commitment in aboutCommitments" :key="commitment.title" class="wl-ac-item">
              <span class="wl-ac-icon"><WlIcon :name="commitment.icon" /></span>
              <div class="wl-ac-copy">
                <strong>{{ commitment.title }}</strong>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>
